<script lang="ts">
	import { page } from '$app/state';
	import AddToWatchlist from '$lib/AddToWatchlist.svelte';
	import BreadCrumb from '$lib/BreadCrumb.svelte';
	import Dynamite from '$lib/Dynamite.svelte';
	import MutualFundNavHeader from '$lib/MutualFundNavHeader.svelte';
	import PriceHeader from '$lib/PriceHeader.svelte';
	import QuoteSearch from '$lib/QuoteSearch.svelte';
	import RegistrationPopup from '$lib/RegistrationPopup.svelte';
	import SideNavigation from '$lib/SideNavigation.svelte';
	import SiderailBanner from '$lib/SiderailBanner.svelte';
	import StickyBanner from '$lib/StickyBanner.svelte';
	import SubNavigation from '$lib/SubNavigation.svelte';
	import TabNavigation from '$lib/TabNavigation.svelte';
	import { getSignedColor } from '$lib/utils';
	import { useQueryClient } from '@tanstack/svelte-query';
	import type { LayoutData } from './$types';

	interface Props {
		data: LayoutData;
		children?: import('svelte').Snippet;
	}

	let { data, children }: Props = $props();
	let { symbol, dividends, mutualFundQuote, quoteData } = $derived(data);

	let navHeaderEl: HTMLElement | undefined = $state();
	let isPinned = $state(false);

	$effect.pre(() => {
		if (navHeaderEl) {
			const observer = new IntersectionObserver(
				([e]) => {
					isPinned = e.intersectionRatio < 1;
					e.target.classList.toggle('is-pinned', e.intersectionRatio < 1);
				},
				{ threshold: [1] }
			);

			observer.observe(navHeaderEl);
		}
	});

	const queryClient = useQueryClient();

	async function wait300ms() {
		return new Promise((resolve) => setTimeout(resolve, 300));
	}
</script>

<RegistrationPopup context="tool" delay={20} />

<div class="lg:page-wrapper">
	<div class="lg:mx-auto lg:flex lg:max-w-screen-xl">
		<div class="page-pb grow basis-0">
			<div class="px-6 lg:px-0">
				<div class="my-4 lg:my-8">
					<BreadCrumb
						breadcrumbs={[{ path: '/securities', title: 'Securities' }, { title: symbol.name }]}
					/>
				</div>
				{#if mutualFundQuote}
					<MutualFundNavHeader {symbol} {mutualFundQuote} />
				{:else if quoteData}
					<PriceHeader
						{symbol}
						price={quoteData.regularMarketPrice}
						changePercent={quoteData.regularMarketChangePercent}
						currency={quoteData.currency}
						lastUpdated={quoteData.regularMarketTime}
					/>
				{/if}
			</div>

			<div bind:this={navHeaderEl} class="nav-header px-6 lg:px-0">
				<div class="stock-label">
					<div
						class="max-w-52 overflow-hidden overflow-ellipsis whitespace-nowrap uppercase lg:max-w-none"
					>
						{symbol.name}
					</div>
					{#if quoteData}
						<div class="ml-5">
							{quoteData.regularMarketPrice}
						</div>
						<div
							style="color: var(--color-{getSignedColor(quoteData.regularMarketChangePercent)})"
							class="small-bold ml-2"
						>
							{quoteData.regularMarketChangePercent.toLocaleString('default', {
								style: 'percent',
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
								signDisplay: 'exceptZero'
							})}
						</div>
					{/if}
				</div>
				<div class="flex">
					<TabNavigation
						navItems={[
							{ label: 'Summary', url: `/quote/${page.params.ticker}` },
							...(dividends?.length
								? [{ label: 'Dividend', url: `/quote/${page.params.ticker}/dividend` }]
								: [])
						]}
					/>
					<div class="border-b-gray-light flex items-center border-b-2 lg:hidden">
						<div class:hidden={!isPinned}>
							<AddToWatchlist {symbol} align="right" silentFollow={true} />
						</div>
					</div>
				</div>
				<div class="flex items-center xl:hidden">
					{#key page.url.pathname}
						{#if !queryClient.isFetching()}
							{#await wait300ms() then}
								<SubNavigation />
							{/await}
						{/if}
					{/key}
					<div class="hidden lg:block">
						<div class:hidden={!isPinned}>
							<AddToWatchlist {symbol} align="right" />
						</div>
					</div>
				</div>
			</div>

			<div class="gap-x-5 px-6 lg:flex lg:px-0">
				<aside class="sticky top-24 mt-6 hidden max-h-screen grow xl:block">
					{#key page.url.pathname}
						{#if !queryClient.isFetching()}
							{#await wait300ms() then}
								<SideNavigation />
							{/await}
						{/if}
					{/key}
				</aside>
				<div class="shrink-0 grow-0 basis-[48rem]">
					{@render children?.()}
				</div>
			</div>
		</div>
		<div
			class="page-pb border-gray-faint ml-5 hidden shrink grow-0 basis-72 border-l-1 pl-5 xl:block"
		>
			<div class="sticky top-0">
				<div class="py-8">
					<QuoteSearch />
				</div>
				<div class="mb-5">
					<SiderailBanner location="stock-tool" />
				</div>
				<Dynamite contextId="2559" />
			</div>
		</div>
	</div>
</div>

<StickyBanner location="stock-tool" />

<style lang="postcss">
	@reference '../../../app.css';

	.stock-label {
		@apply hidden;
	}

	.nav-header {
		@apply bg-white-pure sticky -top-[1px] z-20;

		&:global(.is-pinned) {
			@apply shadow-md lg:shadow-none;

			& .stock-label {
				@apply card-bold flex items-center pt-3;
			}
		}
	}
</style>
